import { useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, HandCoins } from "lucide-react";
import { errMsg, formatDate, useApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateField } from "@/components/common/DateField";
import { money } from "../bookings/helpers";

const day = (v) => v.split("-").reverse().join("/");

function ReturnedDialog({ tid, o, r, onClose, onDone }) {
  const api = useApi();
  const today = format(new Date(), "yyyy-MM-dd");
  const [on, setOn] = useState(today);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/t/${tid}/payments/transactions/${o.intent.id}/refunds/${r.id}/returned`, { returned_on: on, note }); toast.success("Restituzione segnata: nessun denaro è stato trasferito da MUSORA"); onDone(); onClose(); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-md" data-testid="refund-returned-dialog">
        <DialogHeader><DialogTitle>Segnare come restituiti {money(r.amount, o.currency)}?</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Confermi di aver già restituito tu il denaro al cliente (ordine n. {o.number}). MUSORA lo annota soltanto: nessun trasferimento viene eseguito.</p>
        <div className="space-y-1.5 text-sm"><p className="text-xs font-medium text-slate-700">Data della restituzione</p>
          <DateField value={on} onChange={setOn} max={today} clearable={false} label="Data della restituzione" testid="refund-returned-date" /></div>
        <div className="space-y-1.5"><p className="text-xs font-medium text-slate-700">Nota (facoltativa)</p>
          <Input value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="es. bonifico del 12/06, contanti in negozio" data-testid="refund-returned-note" /></div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} data-testid="refund-returned-cancel">Indietro</Button>
          <Button onClick={save} disabled={busy || !on} data-testid="refund-returned-confirm">Segna come restituito</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);
}

// Manual payments (bank transfer / pickup): each registered refund is money the merchant still owes until he marks it returned.
export default function RefundReturns({ tid, o, canMark, onDone }) {
  const [ask, setAsk] = useState(null);
  const list = o.refunds_to_return || [];
  if (!list.length) return null;
  const due = list.filter((r) => !r.returned_at);
  return (
    <div className={`rounded-md border px-3 py-2 text-xs ${due.length ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`} data-testid="order-refund-returns">
      <p className="mb-1 flex items-center gap-1.5 font-semibold text-slate-800"><HandCoins size={14} className={due.length ? "text-amber-600" : "text-slate-400"} />
        {due.length ? `Da restituire al cliente: ${money(due.reduce((t, r) => t + r.amount, 0), o.currency)}` : "Rimborsi registrati: tutti restituiti"}</p>
      <ul className="space-y-1.5">{list.map((r) => (
        <li key={r.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`refund-return-${r.id}`}>
          <span className="min-w-0 text-slate-700">{money(r.amount, o.currency)} · registrato il {formatDate(r.at)}{r.by ? ` da ${r.by}` : ""}
            {r.returned_at ? <span className="ml-1 inline-flex items-center gap-1 text-emerald-700" data-testid={`refund-return-done-${r.id}`}><CheckCircle2 size={12} />restituito il {day(r.returned_on)} (segnato da {r.returned_by}){r.returned_note ? ` · ${r.returned_note}` : ""}</span>
              : <span className="ml-1 font-medium text-amber-700" data-testid={`refund-return-due-${r.id}`}>· da restituire</span>}</span>
          {!r.returned_at && canMark && <Button size="sm" variant="outline" className="h-9" onClick={() => setAsk(r)} data-testid={`refund-return-mark-${r.id}`}>Segna come restituito</Button>}
        </li>))}</ul>
      {ask && <ReturnedDialog tid={tid} o={o} r={ask} onClose={() => setAsk(null)} onDone={onDone} />}
    </div>);
}
